import { useState, useEffect, useMemo } from 'react';
import { PRODUCT_FAMILY_ALL } from '../constants/productFamily';

const DEFAULT_PAGE_SIZE = 25;

const EMPTY_TAB_DATA = {
  guarantee: [],
  cancelled: [],
};

const useMockGuarantees = ({
  productFamily, tab, page, searchFields,
}) => {
  const [loading, setLoading] = useState(false);
  const [tabData, setTabData] = useState(EMPTY_TAB_DATA);

  useEffect(() => {
    import('../data/mock/guaranteesMockData').then(module => {
      setTabData({
        guarantee: module.mockGuaranteePurchases,
        cancelled: module.mockCancelledPurchases,
      });
    });
  }, []);

  const allData = tabData[tab] || [];

  const filteredData = useMemo(() => {
    let result = productFamily === PRODUCT_FAMILY_ALL
      ? allData
      : allData.filter(record => record.productFamily === productFamily);

    if (searchFields) {
      const { vin, dealerNumbers } = searchFields;
      if (vin) {
        const vinLower = vin.toLowerCase();
        result = result.filter(r => {
          const recordVin = tab === 'cancelled'
            ? r.vin
            : r.guaranteeOffer.vehicleInformation.vin;
          return recordVin && recordVin.toLowerCase().includes(vinLower);
        });
      }
      if (dealerNumbers && dealerNumbers.length > 0) {
        const dealerNosLower = dealerNumbers.map(d => d.toLowerCase());
        result = result.filter(r => {
          const dealerNo = tab === 'cancelled'
            ? r.dealerNo
            : r.guaranteeOffer.auctionInformation.dealerNumber;
          return dealerNo && dealerNosLower.includes(dealerNo.toLowerCase());
        });
      }
    }

    return result;
  }, [allData, productFamily, tab, searchFields]);

  const totalCount = filteredData.length;
  const startIndex = (page - 1) * DEFAULT_PAGE_SIZE;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + DEFAULT_PAGE_SIZE
  );
  // TEMP: Simplified mock pagination — this logic will be removed once we switch to the real API.
  // Since this is a mock service, we only prevent pagination beyond the available data boundaries.
  const isLastPage = startIndex + DEFAULT_PAGE_SIZE >= totalCount;
  const startDisplay = totalCount > 0 ? startIndex + 1 : 0;
  const endDisplay = startIndex + paginatedData.length;

  // Simulate loading delay on input changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productFamily, tab, page]);

  return {
    data: paginatedData,
    totalCount,
    loading,
    isLastPage,
    start: startDisplay,
    end: endDisplay,
  };
};

export default useMockGuarantees;
